import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { customFetch } from "./custom-fetch";

export interface ParsedReceiptItem {
  description: string;
  quantity: number;
  unitPrice: number;
}

export interface ParsedReceipt {
  vendor: string | null;
  receiptDate: string | null;
  items: ParsedReceiptItem[];
  subtotal: number | null;
  total: number | null;
}

export interface EstimateMaterial {
  id: number;
  name: string;
  lineItemDescription: string | null;
  quantity: number;
  unitCost: number;
  type: string;
  unit: string | null;
}

export interface CommitReceiptItem {
  name: string;
  vendor?: string | null;
  date?: string | null;
  quantity: number;
  unitCost: number;
  type?: string;
  sourceMaterialId?: number | null;
}

export interface CommitReceiptBody {
  items: CommitReceiptItem[];
}

export interface CommitReceiptResult {
  ok: boolean;
  created: number;
}

export const getEstimateMaterialsQueryKey = (projectId: number) =>
  ["projects", projectId, "estimate-materials"] as const;

export const listEstimateMaterials = (projectId: number) =>
  customFetch<EstimateMaterial[]>(`/api/projects/${projectId}/estimate-materials`);

export const useEstimateMaterials = (projectId: number) =>
  useQuery({
    queryKey: getEstimateMaterialsQueryKey(projectId),
    queryFn: () => listEstimateMaterials(projectId),
  });

export const parseProjectReceipt = (
  projectId: number,
  data: { dataBase64: string; mimeType: string },
) =>
  customFetch<{ receipt: ParsedReceipt }>(`/api/projects/${projectId}/import-receipt`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });

export const useParseProjectReceipt = (projectId: number) =>
  useMutation({
    mutationFn: (data: { dataBase64: string; mimeType: string }) =>
      parseProjectReceipt(projectId, data),
  });

export const commitProjectReceipt = (projectId: number, body: CommitReceiptBody) =>
  customFetch<CommitReceiptResult>(`/api/projects/${projectId}/import-receipt/commit`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

export const useCommitProjectReceipt = (projectId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CommitReceiptBody) => commitProjectReceipt(projectId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [`/api/projects/${projectId}/actual-costs`] });
    },
  });
};
